import CustomModalHeader from 'components/Custom/CustomModalHeader'
import CustomModalFooter from 'components/Custom/CustomModalFooter'
import SubmitModalButton from 'components/Custom/SubmitModalButton'
import CancelModalButton from 'components/Custom/CancelModalButton'
import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormGroup, Input, Modal, ModalBody, Label } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faPlus } from '@fortawesome/free-solid-svg-icons'
import {
  createSlackChannel,
  updateSlackChannel,
  setSlackChannelModal,
  clearSlackChannelErrors,
} from 'store/SlackChannels/actions'

function SlackChannelModal() {
  const dispatch = useDispatch()
  const { modal, createLoading, updateLoading, error } = useSelector(
    (store) => store.slackChannels
  )

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    webhook_url: '',
  })
  const [formErrors, setFormErrors] = useState({})

  useEffect(() => {
    if (modal.show) {
      if (modal.mode === 'edit' && modal.data) {
        setFormData({
          name: modal.data.name || '',
          description: modal.data.description || '',
          webhook_url: modal.data.webhook_url || '',
        })
      } else {
        setFormData({
          name: '',
          description: '',
          webhook_url: '',
        })
      }
      setFormErrors({})
      dispatch(clearSlackChannelErrors())
    }
  }, [modal.show, modal.mode, modal.data, dispatch])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const validateForm = () => {
    const errors = {}

    if (!formData.name.trim()) {
      errors.name = 'Channel name is required'
    }

    if (!formData.webhook_url.trim()) {
      errors.webhook_url = 'Webhook URL is required'
    } else if (
      !formData.webhook_url.startsWith('http://') &&
      !formData.webhook_url.startsWith('https://')
    ) {
      errors.webhook_url = 'Please enter a valid URL'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = () => {
    if (!validateForm()) return

    const submitData = {
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      webhook_url: formData.webhook_url.trim(),
    }

    if (modal.mode === 'create') {
      dispatch(createSlackChannel(submitData))
    } else {
      dispatch(updateSlackChannel(modal.data.id, submitData))
    }
  }

  const handleClose = () => {
    dispatch(setSlackChannelModal({ show: false }))
    setFormData({ name: '', description: '', webhook_url: '' })
    setFormErrors({})
    dispatch(clearSlackChannelErrors())
  }

  const isLoading = createLoading || updateLoading

  return (
    <Modal
      isOpen={modal.show}
      toggle={handleClose}
      size='md'
      centered
      scrollable
    >
      <CustomModalHeader
        title={
          modal.mode === 'create' ? 'Add Slack Channel' : 'Edit Slack Channel'
        }
        toggle={handleClose}
      />
      <ModalBody className='px-4'>
        {error && (
          <div className='alert alert-danger' role='alert'>
            {error}
          </div>
        )}

        <FormGroup>
          <Label for='name'>
            Channel Name <span className='text-danger'>*</span>
          </Label>
          <Input
            id='name'
            name='name'
            placeholder='Enter channel name'
            type='text'
            value={formData.name}
            onChange={handleInputChange}
            invalid={!!formErrors.name}
            disabled={isLoading}
          />
          {formErrors.name && (
            <div className='invalid-feedback d-block'>{formErrors.name}</div>
          )}
        </FormGroup>

        <FormGroup>
          <Label for='description'>Description</Label>
          <Input
            id='description'
            name='description'
            placeholder='Enter channel description (optional)'
            type='textarea'
            rows={3}
            value={formData.description}
            onChange={handleInputChange}
            disabled={isLoading}
          />
        </FormGroup>

        <FormGroup>
          <Label for='webhook_url'>
            Webhook URL <span className='text-danger'>*</span>
          </Label>
          <Input
            id='webhook_url'
            name='webhook_url'
            placeholder='https://hooks.slack.com/services/...'
            type='url'
            value={formData.webhook_url}
            onChange={handleInputChange}
            invalid={!!formErrors.webhook_url}
            disabled={isLoading}
          />
          {formErrors.webhook_url && (
            <div className='invalid-feedback d-block'>
              {formErrors.webhook_url}
            </div>
          )}
        </FormGroup>
      </ModalBody>
      <CustomModalFooter>
        <SubmitModalButton
          customFn={handleSubmit}
          icon={
            <FontAwesomeIcon
              icon={modal.mode === 'create' ? faPlus : faSave}
              className='mr-2'
            />
          }
          text={modal.mode === 'create' ? 'Create Channel' : 'Update Channel'}
          disabled={isLoading}
        />
        <CancelModalButton customFn={handleClose} text='Cancel' />
      </CustomModalFooter>
    </Modal>
  )
}

export default SlackChannelModal
